import { useRef } from "react";
import { Avatar, Spin, Tooltip } from "antd";
import { ArrowLeft, Cake, ExternalLink, ImageIcon, Info, MailWarning, RefreshCw, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

interface FbFinder {
    /** Trang Facebook để extension dò (bình luận / hộp thư / tìm theo tên); rỗng là phải tự mở */
    targetUrl: string;
    /** Đang dò khách này */
    finding: boolean;
    start: (args: { customerId: number; customerName?: string; lastMessage?: string; targetUrl: string }) => void;
}

interface Props {
    /** Khách đang mở (Conversation của danh sách hội thoại) */
    user: any;
    isReadOnly: boolean;
    avatarSrc?: string;
    uploadingAvatar: boolean;
    onChangeAvatar: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /** Link trang cá nhân của khách trên kênh (rỗng là chưa có) */
    profileLink: string;
    goBack: () => void;
    openInfo: (open?: boolean) => void;
    /** Có extension "Khothi FB Finder" và là khách Facebook; null thì ẩn nút dò */
    fbFinder: FbFinder | null;
    onRestoreUnread: () => void;
    restoringUnread: boolean;
}

/** Header khung chat: ảnh đại diện (bấm để đổi), tên khách + link trang cá nhân, nút dò Facebook, khôi phục chưa đọc */
export default function ChatHeader({
    user,
    isReadOnly,
    avatarSrc,
    uploadingAvatar,
    onChangeAvatar,
    profileLink,
    goBack,
    openInfo,
    fbFinder,
    onRestoreUnread,
    restoringUnread,
}: Props) {
    const { t } = useTranslation();
    const avatarInputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="flex items-center justify-between px-4 py-2 border-b border-color-1f1f1f bg-white dark:bg-[#1f1f1f]">

            <div className="flex items-center gap-2">

                {/* Back button (mobile) */}
                <button
                    type="button"
                    aria-label={t("roomchat.back")}
                    onClick={goBack}
                    className="md:hidden mr-1 text-gray-500 hover:text-[#3e91ab]"
                >
                    <ArrowLeft size={20} />
                </button>

                <button
                    type="button"
                    aria-label={t("roomchat.change_avatar")}
                    title={t("roomchat.change_avatar")}
                    className="relative group cursor-pointer shrink-0 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#3e91ab]"
                    onClick={() => avatarInputRef.current?.click()}
                >
                    <Avatar
                        src={avatarSrc}
                        size={40}
                        className="dark:bg-[#2a2a2a]"
                    >
                        {user?.name?.charAt(0) || "?"}
                    </Avatar>

                    <span
                        className={`
                            absolute inset-0
                            rounded-full
                            bg-black/40
                            transition
                            flex items-center
                            justify-center
                            ${uploadingAvatar ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"}
                        `}
                    >
                        {uploadingAvatar
                            ? <Spin size="small" />
                            : <ImageIcon size={16} className="text-white" />}
                    </span>
                </button>

                <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={onChangeAvatar}
                />

                {/* Tên khách + hàng icon thao tác nhanh ngay bên dưới */}
                <div className="flex flex-col min-w-0">

                    {/* Không dùng class .text-000000e0 cho link vì nó set color !important,
                        sẽ chặn màu hover -> đặt màu chữ trực tiếp cho cả sáng/tối */}
                    {profileLink ? (
                        <Tooltip title={profileLink}>
                            <a
                                href={profileLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium flex items-center gap-1 min-w-0 no-underline hover:no-underline text-[#000000e0] dark:text-[#e5e5e5] hover:text-[#3e91ab] dark:hover:text-[#3e91ab] transition-colors"
                            >
                                {/* Mọi kích thước màn hình đều hiện tên (điện thoại rút gọn bằng dấu …) */}
                                <span className="truncate max-w-[160px] sm:max-w-[260px] md:max-w-none">{user.name}</span>
                                <ExternalLink size={14} className="shrink-0 opacity-60" />
                            </a>
                        </Tooltip>
                    ) : (
                        <span className="font-medium text-000000e0 block truncate max-w-[160px] sm:max-w-[260px] md:max-w-none" title={user.name}>
                            {user.name}
                        </span>
                    )}

                    {/* Cập nhật Giới tính & Ngày sinh: mở panel Thông tin khách hàng rồi bật hộp thoại */}
                    {!isReadOnly && (
                        <Tooltip title={t("roomchat.gender_birthday")}>
                            <button
                                type="button"
                                aria-label={t("roomchat.gender_birthday")}
                                onClick={() => {
                                    openInfo?.(true);
                                    window.dispatchEvent(new CustomEvent("open-gender-birthday"));
                                }}
                                className="w-fit text-gray-400 hover:text-[#3e91ab] transition-colors"
                            >
                                <Cake size={14} />
                            </button>
                        </Tooltip>
                    )}
                </div>

                {/* Khách Facebook: extension tự dò link + ảnh đại diện.
                    Vẫn giữ nút cho khách ĐÃ có link để chạy lại lấy ảnh mới - khách hay
                    đổi ảnh, và ảnh cũ lưu trong CMS thì không tự cập nhật theo.
                    Không có extension thì ẩn hẳn cho đỡ rối. */}
                {fbFinder && (
                    <Tooltip
                        title={
                            profileLink
                                ? t("roomchat.fb_refresh_tooltip")
                                : fbFinder.targetUrl
                                    ? t("roomchat.fb_find_tooltip")
                                    : t("roomchat.fb_find_tooltip_manual")
                        }
                    >
                        <button
                            disabled={fbFinder.finding}
                            onClick={() =>
                                fbFinder.start({
                                    customerId: user.id,
                                    customerName: user.name,
                                    lastMessage: user.lastMessage,
                                    targetUrl: fbFinder.targetUrl,
                                })
                            }
                            className="hidden md:flex items-center gap-1 px-2 py-[2px] text-[12px] border rounded-md text-gray-500 hover:text-[#3e91ab] hover:border-[#3e91ab] transition-colors disabled:opacity-60 disabled:cursor-default disabled:hover:text-gray-500"
                        >
                            {fbFinder.finding ? (
                                <>
                                    <Spin size="small" />
                                    {t("roomchat.fb_finding")}
                                </>
                            ) : (
                                <>
                                    {profileLink ? <RefreshCw size={13} /> : <Search size={13} />}
                                    {profileLink ? t("roomchat.fb_refresh") : t("roomchat.fb_find")}
                                </>
                            )}
                        </button>
                    </Tooltip>
                )}

            </div>

            <div className="flex items-center gap-4 text-gray-500">

                {/* Info button (tablet/mobile) */}
                <button
                    type="button"
                    aria-label={t("roomchat.customer_info")}
                    onClick={() => openInfo(true)}
                    className="xl:hidden hover:text-[#3e91ab]"
                >
                    <Info size={18} />
                </button>

                {!isReadOnly && (
                    <>
                        <Tooltip title={t("roomchat.restore_unread_tooltip")}>
                            <button
                                type="button"
                                aria-label={t("roomchat.restore_unread_tooltip")}
                                onClick={onRestoreUnread}
                                disabled={restoringUnread}
                                className="flex text-gray-500 hover:text-[#3e91ab] disabled:opacity-50 disabled:cursor-wait transition-colors"
                            >
                                <MailWarning size={18} />
                            </button>
                        </Tooltip>

                        {/* Tạm ẩn nút "Phân công hội thoại": danh sách nhân viên đang viết cứng và chọn xong
                            không lưu được (chưa có API). Mở lại khi có API phân công thật. */}

                        {/* Tạm ẩn tính năng ghim thông báo (sẽ mở lại sau)
                        <Tooltip title={t("roomchat.pin_tooltip")}>
                            <Pin
                                size={18}
                                onClick={handleTogglePin}
                                className={`
                                    cursor-pointer transition-colors
                                    ${pinning ? "opacity-50 pointer-events-none" : ""}
                                    ${isPinned ? "text-[#3e91ab]" : "text-gray-500 hover:text-[#3e91ab]"}
                                `}
                                fill={isPinned ? "#3e91ab" : "none"}
                            />
                        </Tooltip>
                        */}

                        {/* <Tooltip title={t("roomchat.support_tooltip")}>
                    <Headphones size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                </Tooltip>

                <Tooltip title={t("roomchat.email_tooltip")}>
                    <Mail size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                </Tooltip>

                <Tooltip title={t("roomchat.history_tooltip")}>
                    <Clock size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                </Tooltip>

                <Tooltip title={t("roomchat.copy_link_tooltip")}>
                    <Link size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                </Tooltip> */}
                    </>
                )}

            </div>

        </div>
    );
}
